import React, { useEffect, useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "./crm/LeadForm";

const blank = {
  customer_name: "", company: "", phone: "", alt_phone: "", email: "", gstin: "",
  city: "", state: "", address: "", product: "", notes: "", assigned_to: "",
};

/** Adds a customer straight to the customer list, without creating a lead first. */
export const CustomerForm = ({ open, onOpenChange, onSaved }) => {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState(blank);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(blank);
    if (isAdmin) api.get("/staff/assignable").then((r) => setStaff(r.data)).catch(() => {});
  }, [open, isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/customers", form);
      toast.success(`${data.customer_name} added as customer ${data.customer_no}`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add a customer</DialogTitle>
          <DialogDescription>
            For customers you already deal with. They go straight into the customer list, ready for
            quotations, invoices and demos.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Customer name" required>
              <Input required value={form.customer_name} data-testid="cf-name" onChange={set("customer_name")} />
            </Field>
            <Field label="Company">
              <Input value={form.company} data-testid="cf-company" onChange={set("company")} />
            </Field>
            <Field label="Phone number" required>
              <Input required value={form.phone} data-testid="cf-phone" onChange={set("phone")} />
            </Field>
            <Field label="Alternate phone">
              <Input value={form.alt_phone} onChange={set("alt_phone")} />
            </Field>
            <Field label="Email address">
              <Input type="email" value={form.email} data-testid="cf-email" onChange={set("email")} />
            </Field>
            <Field label="GSTIN">
              <Input value={form.gstin} className="num" onChange={set("gstin")} />
            </Field>
            <Field label="City">
              <Input value={form.city} onChange={set("city")} />
            </Field>
            <Field label="State">
              <Input value={form.state} onChange={set("state")} />
            </Field>
          </div>

          <Field label="Billing address">
            <Textarea rows={2} value={form.address} onChange={set("address")} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Main product interest">
              <Input value={form.product} onChange={set("product")} placeholder="Robusto Cases" />
            </Field>
            {isAdmin && (
              <Field label="Account owner" hint="Defaults to you.">
                <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                  <SelectTrigger data-testid="cf-owner"><SelectValue placeholder="Assign to" /></SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>

          <Field label="Notes">
            <Textarea rows={2} value={form.notes} onChange={set("notes")}
              placeholder="Payment terms, key contacts, anything the team should know" />
          </Field>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="cf-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Add customer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
